import type { AccordionContent } from 'Apps/Products/Blocks/AccordionBlock';

import * as fragrances from 'constants/haircareFragrances';

import acai from 'assets/images/homeRevamp/ingredients/0-acai.png';
import acaciaCollagen from 'assets/images/ingredients/acacia-collagen.png';
import appleCiderVinegar from 'assets/images/ingredients/apple-cider-vinegar.png';
import biotin from 'assets/images/ingredients/biotin.png';
import fermentedRiceWater from 'assets/images/ingredients/fermented-rice-water.png';
import greenTeaWater from 'assets/images/ingredients/green-tea.png';
import jasminWater from 'assets/images/ingredients/jasmin-water.png';
import jujubeBarkWater from 'assets/images/ingredients/jujube-bark-extract.png';
import proVitaminB5 from 'assets/images/ingredients/pro-vitamin-b5.png';
import shikakai from 'assets/images/ingredients/shikakai-extract.png';
import shampoo0 from 'assets/images/productPages/shampoo/shampoo-carousel-0.jpg';
import shampoo0Desktop from 'assets/images/productPages/shampoo/shampoo-carousel-0-d.jpg';
import shampoo1 from 'assets/images/productPages/shampoo/shampoo-carousel-1.jpg';
import shampoo1Desktop from 'assets/images/productPages/shampoo/shampoo-carousel-1-d.jpg';
import shampoo2 from 'assets/images/productPages/shampoo/shampoo-carousel-2.jpg';
import shampoo2Desktop from 'assets/images/productPages/shampoo/shampoo-carousel-2-d.jpg';
import shampoo3 from 'assets/images/productPages/shampoo/shampoo-carousel-3.jpg';
import shampoo3Desktop from 'assets/images/productPages/shampoo/shampoo-carousel-3-d.jpg';
import shampoo4 from 'assets/images/productPages/shampoo/shampoo-carousel-4.jpg';
import shampoo4Desktop from 'assets/images/productPages/shampoo/shampoo-carousel-4-d.jpg';
import shampooHowItWorksPicture from 'assets/images/productPages/shampoo/shampoo-how-to-use.jpg';
import strengthSvgIcon from 'assets/images/productPages/shampoo/strength.svg';

const ticker = [
  'cruelty free',
  'peta approved',
  'alcohol free',
  'sulfate free',
  'paraben free',
  'mineral oil free',
  'phthalate free',
  'gmo free',
  'available fragrance-free',
  'silicone-free options',
  'vegan options',
];

const faq = {
  title: 'FAQs',
  questions: [
    {
      question: 'What shampoo should I use?',
      answer: (
        <>
          Everyone’s hair is unique, and your shampoo should factor in your hair type, porosity,
          density, and the environmental stressors (like hard water, humidity and pollution) in your
          zip code. Prose asks in-depth questions about your diet, lifestyle, and climate, all of
          which impact your hair.
          <br />
          <br />
          From millions of possible formulas, only one is uniquely yours. Take your{' '}
          <a href="/consultation/haircare/my-hair/length" target="_blank">
            consultation
          </a>{' '}
          today to find out what your hair needs.
        </>
      ),
    },
    {
      question: 'What type of hair is this shampoo for?',
      answer: (
        <>
          Prose custom haircare works on straight (type 1), wavy (type 2), curly (type 3) and coily
          (type 4) hair. Our completely personalized formulas account for different hair densities,
          porosities, textures, and types, so your hair can get the exact care that it needs. If you
          aren&#39;t sure what your hair type is, find out in our{' '}
          <a href="/blog/identify-hair-type" target="_blank">
            Hair Types 101
          </a>{' '}
          guide.
        </>
      ),
    },
    {
      question: 'How often should I wash my hair?',
      answer:
        'How often you need to shampoo varies for each individual, since sebum (the natural oils your scalp produces) is impacted by external and internal factors. These factors range from pollution to humidity, and stress levels to hormones. Your personalized Prose formulas can help mitigate environmental stressors and provide the right targeted ingredients to help keep your hair and scalp balanced — which can mean more time between washes!',
    },
    {
      question: 'Will Prose shampoo help me go longer in between washes?',
      answer:
        "This will vary for each individual, since sebum production is impacted by external and internal factors. These can range from pollution and humidity, to stress levels and hormones. Your personalized Prose formulas can help mitigate environmental stressors, but as for fixing stress levels — we're still working on it!",
    },
    {
      question: `I have a sensitive scalp — can I still use Prose Custom Shampoo?`,
      answer:
        'Yes — our personalized shampoo aims to address scalp sensitivity with a gentle formula. Our shampoo can also be made fragrance-free, which we recommend for those who have sensitive skin. Remember, always consult a doctor if you’re experiencing a severe medical condition.',
    },
    {
      question: 'What ingredients should I avoid in shampoo?',
      answer:
        'Prose is not intended to treat medical conditions. Our formulas are a 100% drug-free alternative to prescription products and OTC (over-the-counter) medicines. Instead, we use only the highest quality, naturally powerful and proven-effective ingredients to enhance shine, volume, hair health, and so much more.',
    },
    {
      question: 'What is the best shampoo and conditioner to use together?',
      answer:
        'For the best results, we recommend using Prose Custom Shampoo and Conditioner together to cleanse your scalp and hydrate your hair’s ends. Your hair may need different care on its lengths, and our Custom Shampoo and Custom Conditioner are uniquely formulated for your specific hair type and concerns, to provide personalized care from root to tip.',
    },
  ],
};

const heroAccordion: AccordionContent[] = [
  {
    id: 'custom',
    title: 'HOW PROSE DOES CUSTOM',
    content: {
      list: [
        {
          title: 'Take our haircare consultation',
          description:
            'We’ll analyze 85+ factors — from what you eat to where you live — to understand the unique needs of your hair and scalp.',
        },
        {
          title: 'Your formulas are made to order',
          description:
            'Our AI-powered algorithm creates your personalized formulas + recommended routine, carefully selecting from 185+ clean ingredients to meet your hair goals.',
        },
        {
          title: 'As you change, so does your Prose',
          description:
            'We’ll address any new concerns, adjust your formulas with the seasons, and learn from your feedback for better hair with every order.',
        },
      ],
    },
  },
  {
    id: 'scientificResults',
    title: 'SCIENTIFIC RESULTS',
    content: {
      title:
        'In a rigorous in-house study on Custom Shampoo, we tested its effectiveness across a range of hair types and textures.',
      list: [
        // Alt attributes must remain empty in this case as the images are purely decorative
        {
          icon: strengthSvgIcon,
          text: 'Helps increase hair strength and reduce breakage*',
          alt: '',
        },
      ],
      mentions: [
        '*Instrumental test of Prose Shampoo and Conditioner vs. non-conditioning shampoo',
      ],
    },
  },
  {
    id: 'exclusiveFragrances',
    title: 'EXCLUSIVE FRAGRANCES',
    content: {
      title:
        'During the consultation, you can choose an exclusive fragrance from our clean, 100% vegan collection — or opt for fragrance-free.',
      list: [
        fragrances.BOTANICA,
        fragrances.OASIS,
        fragrances.ARCADIA,
        fragrances.PRELUDE,
        fragrances.MELENI,
        fragrances.CORSICA,
        fragrances.NONE,
      ],
    },
  },
];

const hero = {
  isNew: false,
  title: 'Custom Shampoo',
  subtitle:
    'A gentle, clarifying shampoo, personalized to meet your unique hair and scalp cleansing needs.',
  description: '8.5 fl oz  250mL',
  slider: [
    {
      src: { mobile: shampoo0, desktop: shampoo0Desktop },
      alt: 'Prose Custom Shampoo bottle labeled “for Michelle” standing upright against a beige background.',
    },
    {
      src: { mobile: shampoo1, desktop: shampoo1Desktop },
      alt: 'A Prose Custom Shampoo bottle lying on its side with clear shampoo covering the label, highlighting the consistency.',
    },
    {
      title: 'Proven to increase hair strength + reduce breakage',
      subtitle:
        '*Instrumental test of Prose Custom Shampoo & Conditioner vs non-conditioning shampoo.',
      src: { mobile: shampoo2, desktop: shampoo2Desktop },
      alt: 'A person rinsing their hair in the shower while lathered with Prose Custom Shampoo, featuring a claim about improved hair strength.',
    },
    {
      src: { mobile: shampoo3, desktop: shampoo3Desktop },
      alt: 'A Prose Custom Shampoo bottle labeled “for Dreya” covered in foam and surrounded by bubbles.',
    },
    {
      src: { mobile: shampoo4, desktop: shampoo4Desktop },
      alt: 'Four individuals smiling and holding Prose Custom Shampoo bottles, showcasing personalized hair care for different hair types.',
    },
  ],
  personalizationWorksBetter: {
    title: (
      <>
        personalization works better<sup>®</sup>
      </>
    ),
    description:
      'From rebalancing roots to gently hydrating lengths, Prose Custom Shampoo is specifically formulated to target multiple concerns, including oily hair, dry scalps, and more. Used consistently, it’s proven to increase hair strength and reduce breakage.* ',
    disclaimer: '*Instrumental test of Prose Shampoo and Conditioner vs. non-conditioning shampoo',
    reassurances: ['Sulfate-free', 'Color-safe', 'Keratin-safe', 'Non-stripping'],
  },
  accordion: heroAccordion,
  upsellModule: null,
};

const howToUse = {
  label: 'How to use',
  title: 'A shampoo as unique as you are',
  imageAlt: 'Model holding a bottle of Prose Custom Shampoo.',
  description:
    'Each Custom Shampoo includes directions tailored to your specific hair, goals, and concerns — from oily roots to dry ends. Below is just a guide.',
  amount: 'As needed, based on hair length, texture, and density.',
  steps: [
    'Begin with damp hair and dispense desired amount of shampoo onto fingertips.',
    'Rub together, adding small amounts of water and gently massage into the hair and scalp.',
    'Focus on the roots to create a rich lather.',
    'Rinse thoroughly and repeat if desired. Follow with your Custom Conditioner as needed.',
  ],
  video: 'how-to-use-shampoo',
  posterImage: shampooHowItWorksPicture,
};

const reviewsSlide1 = {
  name: 'Carmen V.',
  date: '10/22/24',
  location: 'Waco, TX',
  stars: '★★★★★',
  text: `I'm super happy with my shampoo formula. My hair gets clean even without being stripped and it's gentle on my color treated hair. I find I don't have to use a ton of product to get a proper lather and good coverage on my strands even as they lengthen. My hairstylist has asked me to remind her of the product I've been using because she likes how healthy my hair has looked. So please know this formula is definitely spot on.`,
};

const reviewsSlide2 = {
  name: 'Olivia P.',
  date: '3/13/24',
  location: 'Cheyenne, WY',
  stars: '★★★★★',
  text: `I love the shampoo. My hair feels soft and clean. I use demi-permanant hair color that slowly washes out, so I avoid harsh roots. Salon brands would wash the color out quickly, but my Prose shampoo is making my color last longer, and look richer.`,
};

const reviewsSlide3 = {
  name: 'Hana E.',
  date: '3/15/24',
  location: 'Point Marion, PA',
  stars: '★★★★★',
  text: `This is amazing. I really tried everything else, every professional brand, and spent hundreds per bottle, but Prose is made with REAL ingredients and causes absolutely no damage. My hair is smooth, no frizz. It’s way beyond any hydrating I’ve ever gotten from any other shampoo. I’m speechless right now. I’m just mad at myself I didn’t try this sooner and wasted so much money on different brands. Prose, you saved my hair, my confidence has been through the roof. I can’t thank you enough!!`,
};

const reviewsSlide4 = {
  name: 'Ashley L.',
  date: '3/6/24',
  location: 'Fairview, OK',
  stars: '★★★★★',
  text: `My husband loves running his fingers through my long hair, and since I’ve been using it, he can’t quit touching my hair. He has always loved how my hair smells but now he loves sleeping on my pillow because it smells like my hair. Ladies, you won’t regret this investment. Not only will you love it, HE WILL LOVE IT AS WELL❤️`,
};

const reviewsSlide5 = {
  name: 'Lauren T.',
  date: '3/27/24',
  location: 'Blue Springs, MO',
  stars: '★★★★★',
  text: `Previously, my hair would lose curl throughout the day and be stringy on day 2. With Prose, I feel like I can still rock curls on day 2 without having to wash my hair every day. I do think it has helped tame frizz and maintain my curls for a longer period of time. Also, I LOVE the smell!`,
};

const reviewsSlide6 = {
  name: 'Nicole B.',
  date: '8/3/24',
  location: 'Centerburg, OH',
  stars: '★★★★★',
  text: `I was super skeptical about Prose products working for me since I’d spent a crazy amount of money trying nearly every other hair product on the market with little to no results. I have been absolutely blown away by the positive changes with my hair since using Prose. My once frizzy, tangled, thinning, blah hair is now full and bouncy, and the healthiest it’s looked in YEARS. I have gotten so many compliments (including from my hairdresser!) on the condition of my hair and how amazing it’s looking. I don’t typically write reviews but I honestly can’t express enough how very happy I am with this product. The shampoo is super light, which made me worried it wasn’t going to suds up how I like, but it gets super sudsy and leaves my hair feeling all-the-way clean.`,
};

const reviewsSlide7 = {
  name: 'Isabel F.',
  date: '9/6/24',
  location: 'Poland, NY',
  stars: '★★★★★',
  text: `This is the first shampoo I’ve ever used in my adult life that hasn’t burned my scalp. On top of that, shampoo used to burn my scalp but I would STILL have flakiness and dandruff as soon as I got out of the shower, making me feel like it was all for naught. Not only does this shampoo take away all itchiness and flakiness I’ve always dealt with, but it is also the first time I’ve seen a pale normal colored scalp instead of inflamed red or pink. And dear god what kind of magic have you done to make it smell so god damn good?! Seriously, if I could bottle the smell up and use it for everything I would. Even after my hair has been dried for a day my husband would catch a whiff and say this is the best my hair has ever smelled. So thanks for that too! Please don’t ever stop making this shampoo because I think I’d die if I have to go back to itchy, inflamed, red scalp that doesn’t smell like the garden of Eden.`,
};

const reviewsSlide8 = {
  name: 'Monique D.',
  date: '8/15/24',
  location: 'North Kingstown, RI',
  stars: '★★★★',
  text: `I really like this shampoo. It lathers on very nicely. Following the directions as advised, I can see and feel the difference from just one wash!`,
};

const reviewsSlide9 = {
  name: 'Marisol C.',
  date: '7/19/24',
  location: 'North Hills, CA',
  stars: '★★★★',
  text: `My hair feels cleaner for longer. Previously, my hair would start to feel oily and unclean 2 days after washing. Now, I can go 3 days, sometimes even 4, before it feels gross.`,
};

const reviewsSlide10 = {
  name: 'Maya L.',
  date: '8/20/24',
  location: 'Long Grove, IA',
  stars: '★★★★',
  text: `I absolutely love this shampoo that is developed JUST FOR ME! I was always a salon shampoo buyer until I decided to try Prose. I am thrilled and will not go back.`,
};

const reviewsSlide11 = {
  name: 'Taylor C.',
  date: '8/20/24',
  location: 'Havertown, PA',
  stars: '★★★★',
  text: `I love the smell and the clean feeling it leaves. It lathers awesome for my almost waist-length hair.`,
};

const reviewsSlide12 = {
  name: 'Jordan M.',
  date: '9/11/24',
  location: 'Cottage Grove, WI',
  stars: '★★★',
  text: `The shampoo works wonders for giving me that clean-hair feeling without feeling stripped. However, my hair still feels dry midback and to the ends of my hair. It does have nice shine without being too shiny that it looks dirty. My hair still gets knotty and I have a bit of a dry scalp/some flakiness from time to time (unsure if weather is affecting that with seasons changing) with breakage at the ends. Overall, this shampoo is better than the cheap shampoo brand I used to use before using Prose Custom Shampoo for my hair.`,
};

const reviewsSlide13 = {
  name: 'Rachel N.',
  date: '9/23/24',
  location: 'Oswego, NY',
  stars: '★★★',
  text: `The shampoo cleans well and keeps me oil-free for 2-3 days after washing, which is great. It doesn’t feel as hydrating as previous shampoo orders from Prose, but I believe that this may help with extended periods between washes and keeping oil down. Previous shampoo orders seemed to leave my hair feeling softer immediately after rinsing, while this one seems to be more "squeaky clean" feeling. Not much, but something I noticed. I think that if the conditioner had more slip and hydration to it, this particular shampoo formula may not be the problem.`,
};

const reviewsSlide14 = {
  name: 'Allison A.',
  date: '8/5/24',
  location: 'New Westminster, BC',
  stars: '★★★',
  text: `My hair feels so wonderful! However, I would love more scalp soothing ingredients. The flakes are better. The itch is mildly better.`,
};

const productReviews = {
  title: 'Real people, real reviews',
  description:
    'See what people are saying about Custom Shampoo on Review & Refine®. Our unique feedback tool gathers validated customer reviews for always-improving results.',
  reviews: [
    reviewsSlide1,
    reviewsSlide2,
    reviewsSlide3,
    reviewsSlide4,
    reviewsSlide5,
    reviewsSlide6,
    reviewsSlide7,
    reviewsSlide8,
    reviewsSlide9,
    reviewsSlide10,
    reviewsSlide11,
    reviewsSlide12,
    reviewsSlide13,
    reviewsSlide14,
  ],
};

export const revampShampooPageContent = {
  faq,
  hero,
  howToUse,
  ticker,
  productReviews,
  productCarousel: { headline: 'Complete your haircare routine' },
  ingredientsSlider: {
    titleBlock: {
      title: 'Naturally powerful ingredients, picked just for you',
      subtitle:
        'Below are just a few possibilities. Your Custom Shampoo is made to order using a unique blend of only the clean, proven-effective ingredients your hair needs.',
    },
    sliderCardsContent: [
      {
        ingredientName: 'Fermented Rice Water',
        description: 'to smooth and strengthen hair, controlling frizz',
        img: {
          imgSrc: fermentedRiceWater,
          imgAlt: 'Rice peptides to help improve the appearance of thinning hair',
        },
      },
      {
        ingredientName: 'Apple Cider Vinegar',
        description: 'to enhance shine and promote hair health',
        img: {
          imgSrc: appleCiderVinegar,
          imgAlt: 'Apple Cider Vinegar to enhance shine and promote hair health',
        },
      },
      {
        ingredientName: 'Hyaluronic Acid',
        description: 'to hydrate and protect dry hair',
        img: { imgSrc: acai, imgAlt: 'Hyaluronic acid for nourish and hydrate dry scalps' },
      },
      {
        ingredientName: 'Acacia Collagen',
        description: 'to help protect and hydrate damaged hair',
        img: {
          imgSrc: acaciaCollagen,
          imgAlt: 'Acacia Collagen to help protect and hydrate damaged hair',
        },
      },
      {
        ingredientName: 'Pro-Vitamin B5',
        description: 'to help rebalance the scalp for less sensitivity',
        img: {
          imgSrc: proVitaminB5,
          imgAlt: 'Pro-Vitamin B5 to help rebalance the scalp for less sensitivity',
        },
      },
      {
        ingredientName: 'Shikakai Extract',
        description: 'to gently cleanse and help detoxify roots and scalp',
        img: {
          imgSrc: shikakai,
          imgAlt: 'Shikakai Extract to gently cleanse and help detoxify roots and scalp',
        },
      },
      {
        ingredientName: 'Biotin',
        description: 'to fortify hair strength and boost density',
        img: {
          imgSrc: biotin,
          imgAlt: 'Biotin to fortify hair strength and boost density',
        },
      },
      {
        ingredientName: 'Green Tea Water',
        description: 'to help combat oily roots and oily hair',
        img: {
          imgSrc: greenTeaWater,
          imgAlt: 'Green Tea Water to help combat oily roots and oily hair',
        },
      },
      {
        ingredientName: 'Jasmine Water',
        description: 'to help maintain overall scalp and hair health',
        img: {
          imgSrc: jasminWater,
          imgAlt: 'Jasmine Water to help maintain overall scalp and hair health',
        },
      },
      {
        ingredientName: 'Jujube Bark Extract',
        description: 'to help control flakes and support scalp health',
        img: {
          imgSrc: jujubeBarkWater,
          imgAlt: 'Jujube Bark Extract to help control flaky scalps and calm irritation',
        },
      },
    ],
  },
};
